import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getUnreadCountByObra,
  getUnreadMentionCountByObra,
  getUnreadThreadsForUser,
} from '@/features/comunicacao/services/notificationsService';
import {
  notificationsPreferences,
  readMentionsPreference,
  readPreferredObras,
} from '@/features/comunicacao/utils/notificationPreferences';
import { supabase } from '@/features/sistema/services/supabaseClient';

type ObraId = string | 'ALL';

type NotificationCounter = {
  unread: number;
  mentions: number;
};

type ResolvedNotificationUser = {
  authId: string;
  usuarioId: string | null;
};

export function useNotificationCount(obraId: ObraId = 'ALL') {
  const [counters, setCounters] = useState<NotificationCounter>({ unread: 0, mentions: 0 });
  const [onlyMentionsMode, setOnlyMentionsMode] = useState<boolean>(() => readMentionsPreference());
  const [obraScope, setObraScope] = useState<string[] | null>(() => {
    const stored = readPreferredObras();
    return stored.length ? stored : null;
  });
  const resolvedUserRef = useRef<ResolvedNotificationUser | null>(null);

  const fetchUserContext = useCallback(async (): Promise<ResolvedNotificationUser | null> => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user) {
      resolvedUserRef.current = null;
      return null;
    }

    const authUser = data.user;

    if (resolvedUserRef.current?.authId === authUser.id) {
      return resolvedUserRef.current;
    }

    const metadata = (authUser.user_metadata ?? {}) as Record<string, any>;
    let usuarioId: string | null = null;

    if (typeof metadata.usuario_id === 'string' && metadata.usuario_id.length > 0) {
      usuarioId = metadata.usuario_id;
    } else {
      const email = (authUser.email ?? metadata.email ?? null) as string | null;
      if (email) {
        try {
          const { data: usuarioRows, error: usuarioError } = await supabase
            .from('usuarios')
            .select('id')
            .eq('email', email)
            .limit(1);

          if (usuarioError) {
            console.warn('[useNotificationCount] erro ao buscar usuarios.id', usuarioError);
          } else {
            const row = Array.isArray(usuarioRows) ? usuarioRows[0] : usuarioRows;
            if (row?.id) {
              usuarioId = row.id as string;
            }
          }
        } catch (lookupError) {
          console.warn('[useNotificationCount] erro ao resolver usuarios.id', lookupError);
        }
      }
    }

    const resolved = { authId: authUser.id, usuarioId };
    resolvedUserRef.current = resolved;
    return resolved;
  }, []);

  const fetchCounters = useCallback(
    async (resolvedUser?: ResolvedNotificationUser | null) => {
      const userContext =
        resolvedUser ?? resolvedUserRef.current ?? (await fetchUserContext());

      if (!userContext) {
        setCounters({ unread: 0, mentions: 0 });
        return;
      }

      const usuarioId = userContext.usuarioId ?? userContext.authId;

      if (!usuarioId) {
        setCounters({ unread: 0, mentions: 0 });
        return;
      }

      if (obraId !== 'ALL' && !obraId) {
        setCounters({ unread: 0, mentions: 0 });
        return;
      }

      const targetObraId = obraId !== 'ALL' && obraId ? obraId : null;

      try {
        if (targetObraId) {
          // Alinhar com regra do backend (RPC por obra)
          const [unreadCount, mentionCount] = await Promise.all([
            getUnreadCountByObra(targetObraId, usuarioId),
            getUnreadMentionCountByObra(targetObraId, usuarioId),
          ]);
          const unreadValue = onlyMentionsMode ? mentionCount : unreadCount;
          setCounters({
            unread: unreadValue,
            mentions: mentionCount,
          });
          return;
        }

        const scopedObraIds =
          obraScope && obraScope.length ? Array.from(new Set(obraScope.filter(Boolean))) : null;

        if (scopedObraIds && scopedObraIds.length) {
          const results = await Promise.allSettled(
            scopedObraIds.map(async (id) => {
              const [unreadCount, mentionCount] = await Promise.all([
                getUnreadCountByObra(id, usuarioId),
                getUnreadMentionCountByObra(id, usuarioId),
              ]);
              return { unreadCount, mentionCount };
            }),
          );

          let unreadTotal = 0;
          let mentionTotal = 0;
          results.forEach((result) => {
            if (result.status === 'fulfilled') {
                unreadTotal += result.value.unreadCount ?? 0;
                mentionTotal += result.value.mentionCount ?? 0;
            }
          });

          setCounters({
            unread: onlyMentionsMode ? mentionTotal : unreadTotal,
            mentions: mentionTotal,
          });
          return;
        }

        const { unreadThreadIds, mentionThreadIds } = await getUnreadThreadsForUser({
          usuarioId,
          obraId: null,
          includeMentions: true,
        });

        const unreadCount = onlyMentionsMode ? mentionThreadIds.length : unreadThreadIds.length;
        setCounters({
          unread: unreadCount,
          mentions: mentionThreadIds.length,
        });
      } catch (error) {
        console.warn('[useNotificationCount] erro ao calcular n�o lidas', error);

        setCounters({
          unread: 0,
          mentions: 0,
        });
      }
    },
    [obraId, fetchUserContext, obraScope, onlyMentionsMode],
  );

  useEffect(() => {
    fetchCounters();
  }, [fetchCounters]);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let active = true;

    (async () => {
      const user = await fetchUserContext();
      if (!user || !active) return;

      const targetUsuarioId = user.usuarioId ?? user.authId;
      if (!targetUsuarioId) return;

      channel = supabase
        .channel(`notif-counter-${obraId}-${targetUsuarioId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'app_thread_reads',
            filter: `usuario_id=eq.${targetUsuarioId}`,
          },
          () => fetchCounters(user),
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'app_comment_threads',
          },
          () => fetchCounters(user),
        )
        .subscribe();
    })();

    return () => {
      active = false;
      if (channel) {
        channel.unsubscribe();
      }
    };
  }, [obraId, fetchCounters, fetchUserContext]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePrefChange = (event: Event) => {
      const detail = (event as CustomEvent<boolean | undefined>).detail;
      if (typeof detail === 'boolean') {
        setOnlyMentionsMode(detail);
        return;
      }
      setOnlyMentionsMode(readMentionsPreference());
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === notificationsPreferences.mentionsKey) {
        setOnlyMentionsMode(event.newValue === '1');
      }
    };
    window.addEventListener(
      notificationsPreferences.mentionsEvent,
      handlePrefChange as EventListener,
    );
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(
        notificationsPreferences.mentionsEvent,
        handlePrefChange as EventListener,
      );
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncPreferredObras = () => {
      const stored = readPreferredObras();
      setObraScope(stored.length ? stored : null);
    };

    syncPreferredObras();

    const handlePreferredEvent = (event: Event) => {
      const detail = (event as CustomEvent<string[] | undefined>).detail;
      if (Array.isArray(detail)) {
        setObraScope(detail.length ? detail : null);
        return;
      }
      syncPreferredObras();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === notificationsPreferences.preferredObrasKey) {
        syncPreferredObras();
      }
    };

    window.addEventListener(
      notificationsPreferences.preferredObrasEvent,
      handlePreferredEvent as EventListener,
    );
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(
        notificationsPreferences.preferredObrasEvent,
        handlePreferredEvent as EventListener,
      );
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleRefresh = () => {
      fetchCounters();
    };
    window.addEventListener(notificationsPreferences.refreshCountersEvent, handleRefresh);
    return () => window.removeEventListener(notificationsPreferences.refreshCountersEvent, handleRefresh);
  }, [fetchCounters]);

  return {
    unread: counters.unread,
    mentions: counters.mentions,
    total: counters.unread,
    refetch: fetchCounters,
  };
}
